import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import StarIcon from "../../../elements/V2/Icons/StarIcon"
import Section from "../../../elements/V2/Section"
import Typography from "../../../elements/V2/Typography"
import Video from "../../../experiments/Video"
import HeroLayout from "../HeroLayout"

import { CTATrackingEvent } from "@components/elements/Link"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import MasonryGrid from "@components/V2/LayoutComponents/MasonryGrid"
import { useElementObserver } from "@hooks/V2/useElementObserver"
import { usePageLayoutState } from "@utils/layout"
import {
  getBackgroundColorClass,
  getAccentColor,
  getGradientFromColorClass,
  getGradientToColorClass,
  getGradientViaColorClass,
} from "@utils/V2/color"
import React, { useEffect, useRef } from "react"

type Props = {
  categoryIcon?: string
  categoryTitle?: string
  headingText: string
  headingSize?: "display1" | "h1" | "h2" | "h3" | "h4" | "h5"
  headingMobileAlignment?: "left" | "center"
  bodyText: string
  bodyHighlightedPhrase?: string
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  primaryButtonText?: string
  primaryButtonLink?: string | YocoCom.YocoLink
  primaryButtonMobileLink?: string | YocoCom.YocoLink
  primaryButtonColor: Color
  secondaryButtonText: string
  secondaryCTATextColor: Color.White | Color.Charcoal
  secondaryButtonLink: string | YocoCom.YocoLink | undefined
  imageUrl: string
  imageAlt: string
  coverAssets?: Storyblok.Asset[]
  videoUrl?: string
  trustText?: string
  trustCTAText?: string
  trustCTALink?: string | YocoCom.YocoLink
  hasScrollArrow?: boolean
  hideMobileStickyButton?: boolean
  secondaryTrackingEvent: string
  secondaryTrackingEventKey?: string
  secondaryTrackingEventValue?: string
} & Partial<CTATrackingEvent>

const IndustryHero = ({
  categoryIcon,
  categoryTitle,
  headingText,
  headingSize = "display1",
  headingMobileAlignment = "left",
  bodyText,
  bodyHighlightedPhrase = "",
  primaryBackgroundColor,
  secondaryBackgroundColor,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonMobileLink,
  primaryButtonColor,
  secondaryButtonText,
  secondaryCTATextColor = Color.Charcoal,
  secondaryButtonLink,
  imageUrl,
  imageAlt,
  coverAssets,
  videoUrl,
  trustText,
  trustCTAText,
  trustCTALink,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  hasScrollArrow = false,
  hideMobileStickyButton,
  secondaryTrackingEvent,
  secondaryTrackingEventKey,
  secondaryTrackingEventValue,
  ...props
}: Props) => {
  const textContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(textContainerRef)
  const accentColor = getAccentColor(primaryBackgroundColor)

  const { setHeroCTAVisible, hasAnchorNavigation } = usePageLayoutState()
  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)

    return () => {
      setHeroCTAVisible(false)
    }
  }, [isCtaVisible, setHeroCTAVisible])

  startObserving()

  return (
    <HeroLayout
      primaryButtonText={primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonMobileLink}
      primaryButtonColor={primaryButtonColor}
      hideMobileButton={hideMobileStickyButton}
      trackingEvent={trackingEvent}
      trackingEventKey={trackingEventKey}
      trackingEventValue={trackingEventValue}
    >
      <div
        className={classNames(
          "pb-lg -mb-lg grid items-center pt-0 sm:grid-cols-2",
          !secondaryBackgroundColor
            ? getBackgroundColorClass(primaryBackgroundColor)
            : [
                "bg-gradient-to-br",
                getGradientFromColorClass(primaryBackgroundColor),
                getGradientViaColorClass(secondaryBackgroundColor),
                getGradientToColorClass(secondaryBackgroundColor),
              ]
        )}
        {...props}
      >
        <Section
          className={classNames(
            "flex flex-col pb-16 sm:pb-72 md:pt-navHeight-md lg:pt-navHeight-lg",
            hasAnchorNavigation ? "pt-24" : "pt-navHeight-xs"
          )}
        >
          <div
            className={classNames(
              "flex flex-col sm:items-start sm:text-left",
              headingMobileAlignment === "left"
                ? "items-start"
                : "items-center text-center"
            )}
          >
            {categoryIcon && categoryTitle && (
              <div className="flex w-fit flex-row items-center justify-center gap-x-4 pb-24">
                <GatsbyStoryblokImage
                  aspectRatio="1:1"
                  height={20}
                  width={20}
                  loadType="eager"
                  image={categoryIcon}
                  alt="catergory icon"
                  quality={80}
                />
                <Typography
                  text={categoryTitle}
                  font="grotesk"
                  weight="book"
                  size="subscript-lg"
                  color={accentColor}
                />
              </div>
            )}

            <div className="gap-18 flex flex-col md:gap-24">
              <Typography
                text={headingText}
                font="grotesk"
                weight="medium"
                size={headingSize}
                color={accentColor}
                element="h1"
              />

              {bodyHighlightedPhrase &&
              bodyText.includes(bodyHighlightedPhrase) ? (
                <Typography
                  color={accentColor}
                  text={bodyText}
                  font="grotesk"
                  size="body-md"
                  weight="book"
                  highlightColor={Color.Blue}
                  highlightedText={bodyHighlightedPhrase}
                  highlightSize="body-md"
                  highlightWeight="medium"
                />
              ) : (
                <Typography
                  color={accentColor}
                  text={bodyText}
                  font="grotesk"
                  size="body-md"
                  weight="book"
                />
              )}
            </div>
          </div>

          <div
            ref={textContainerRef}
            className="mt-20 flex flex-col items-center gap-24 sm:mt-40 sm:flex-row sm:items-baseline"
          >
            {primaryButtonText && primaryButtonLink ? (
              <PillButton
                size="large"
                style="solid"
                text={primaryButtonText}
                color={primaryButtonColor}
                linkUrl={primaryButtonLink}
                mobileLinkUrl={primaryButtonMobileLink}
                trackingEvent={trackingEvent}
                trackingEventKey={trackingEventKey}
                trackingEventValue={trackingEventValue}
                hasScrollArrow={hasScrollArrow}
              />
            ) : null}
            <TextButton
              style="text"
              text={secondaryButtonText}
              linkUrl={secondaryButtonLink}
              color={secondaryCTATextColor}
              trackingEvent={secondaryTrackingEvent}
              trackingEventKey={secondaryTrackingEventKey}
              trackingEventValue={secondaryTrackingEventValue}
            />
          </div>

          {trustText && trustCTAText && trustCTALink && (
            <div className="mt-24 hidden flex-col md:flex">
              <div className="flex w-fit flex-row items-center justify-center gap-x-4">
                <StarIcon color={Color.Black} />
                <Typography
                  color={Color.Black}
                  text={trustText}
                  font="grotesk"
                  size="body-sm"
                  weight="medium"
                />
              </div>
              <TextButton
                style="icon"
                text={trustCTAText}
                linkUrl={trustCTALink}
                color={Color.Blue}
                textSize="body-sm"
                icon={
                  <ArrowIcon width="14px" height="14px" className="-mt-[1px]" />
                }
                className="mt-4"
              />
            </div>
          )}
        </Section>
        <div className="pb-40 pr-0 sm:py-60 md:py-72 md:pr-20">
          {videoUrl && coverAssets && coverAssets.length > 0 ? (
            <MasonryGrid
              className="mx-10 max-w-[600px] justify-self-center md:mx-0"
              mobileColumns={2}
            >
              <div>
                <Video
                  video={videoUrl}
                  loadType="eager"
                  aspectRatio="1:1"
                  playOnMobile={true}
                  coverImage={coverAssets[1].filename}
                  playbackMode="autoplay"
                  ImageAlt={imageAlt || "Hero image"}
                  rounded
                />
              </div>

              <GatsbyStoryblokImage
                aspectRatio="1:1"
                loadType="eager"
                image={coverAssets[0].filename}
                alt={imageAlt || "Hero image"}
                quality={80}
                rounded
              />
              <GatsbyStoryblokImage
                aspectRatio="1:1"
                loadType="eager"
                image={coverAssets[1].filename}
                alt={imageAlt || "Hero image"}
                quality={80}
                rounded
              />
              <GatsbyStoryblokImage
                aspectRatio="1:1"
                loadType="eager"
                image={coverAssets[2].filename}
                alt={imageAlt || "Hero image"}
                quality={80}
                rounded
              />
            </MasonryGrid>
          ) : (
            <GatsbyStoryblokImage
              aspectRatio="1:1"
              loadType="eager"
              image={imageUrl}
              alt={imageAlt || "Hero image"}
              quality={80}
            />
          )}
        </div>
        {trustText && trustCTAText && trustCTALink && (
          <div className="mt-20 flex flex-col px-16 pb-24 md:hidden">
            <div className="flex w-fit flex-row items-center justify-center gap-x-4">
              <StarIcon color={Color.Black} />
              <Typography
                color={Color.Black}
                text={trustText}
                font="grotesk"
                size="body-sm"
                weight="medium"
              />
            </div>
            <TextButton
              style="icon"
              text={trustCTAText}
              linkUrl={trustCTALink}
              color={Color.Blue}
              textSize="body-sm"
              icon={
                <ArrowIcon width="14px" height="14px" className="-mt-[1px]" />
              }
              className="mt-4"
            />
          </div>
        )}
      </div>
    </HeroLayout>
  )
}

export default IndustryHero
